import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Avatar, IconButton, TextField,
  Stack, Badge, Divider, CircularProgress, Tooltip, Button,
  Dialog, DialogContent
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ForumIcon from '@mui/icons-material/Forum';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import { sendMessage, toggleChat, setCurrentChat, getChats, deleteChat, markAsRead } from '../../actions/chat';
import { urlImg } from '../common/helpers';
import BotonAdjuntar from './BotonAdjuntar';
import VistaPreviaAdjuntos from './VistaPreviaAdjuntos';
import AdjuntosMensaje from './AdjuntosMensaje';
import { useAdjuntos } from './useAdjuntos';
import MenuChat from './MenuChat';
import AvisoBloqueo from './AvisoBloqueo';
import { MAX_TEXTO_CHAT } from './limites';
import { etiquetaDia, fechaEnLista, horaMensaje, mismoDia } from './fechas';

// Nombre legible del tema de un chat (servicio/página) y su categoría de respaldo
const temaTitulo = (chat) => chat?.tema?.nombre || chat?.tema?.categoria || '';
// Subtítulo con la categoría solo cuando aporta información nueva respecto al título
const temaCategoria = (chat) => (chat?.tema?.nombre && chat?.tema?.categoria) ? chat.tema.categoria : '';

const ChatFloating = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, currentChat, messages, chats, loading, onlineUsers, presenciaRecibida, error: errorChat } = useSelector(state => state.chat);
  const authUser = useSelector(state => state.user.user);
  
  const getStrId = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return id._id.toString();
    return String(id);
  };

  const otherParticipant = currentChat?.participants.find(p => getStrId(p._id) !== getStrId(authUser?._id));
  const isOnline = otherParticipant && onlineUsers.some(id => getStrId(id) === getStrId(otherParticipant._id));
  // El proveedor es quien puede cotizar; la cotización formal solo existe en el chat completo
  const soyProveedor = currentChat && getStrId(currentChat.provider) === getStrId(authUser?._id);
  const soyCliente = currentChat && !soyProveedor;
  // El cliente solo puede abrir/ver la cotización desde el chat completo
  const hayCotizacion = messages.some(m => m.type === 'quote');
  // Mismo criterio que las reservas: el proveedor ve la ficha del cliente y el cliente el perfil público del proveedor
  const rutaPerfil = otherParticipant?._id
    ? `${soyProveedor ? '/user/cliente' : '/perfil'}/${getStrId(otherParticipant._id)}`
    : null;

  const irChatCompleto = () => {
    navigate('/mensajes');
    dispatch(toggleChat(false));
  };

  // La ventana flotante taparía el perfil (en celular ocupa casi todo el ancho). La conversación
  // queda abierta al volver a abrir la burbuja. Con ctrl/⌘/shift el perfil se abre en otra pestaña
  // y aquí el chat se queda como está.
  const alAbrirPerfil = (e) => {
    if (!e.ctrlKey && !e.metaKey && !e.shiftKey) dispatch(toggleChat(false));
  };
  
  const [text, setText] = useState('');
  const [deletingChat, setDeletingChat] = useState(null);
  const messagesEndRef = useRef(null);
  const adj = useAdjuntos();
  const [enviando, setEnviando] = useState(false);
  const { limpiar: limpiarAdjuntos } = adj;

  // Los archivos elegidos son de UNA conversación: al cambiar de chat se descartan para no mandarlos a otra persona
  useEffect(() => { limpiarAdjuntos(); }, [currentChat?._id, limpiarAdjuntos]);

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 100);
  };

  const totalUnread = chats.reduce((acc, chat) => {
    const myId = getStrId(authUser?._id);
    const count = chat.unreadCount?.[myId] || 0;
    return acc + count;
  }, 0);

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      dispatch(getChats());
    }
  }, [isOpen, dispatch]);

  useEffect(() => {
    if (isOpen && messages.length > 0) {
      scrollToBottom();
    }
    if (isOpen && currentChat) {
      dispatch(markAsRead(currentChat._id));
    }
  }, [messages, isOpen, currentChat, dispatch]);

  const handleSend = async (e) => {
    e.preventDefault();
    const archivos = adj.archivos;
    if (!currentChat || enviando || adj.procesando || (!text.trim() && archivos.length === 0)) return;
    const contenido = text;
    setText('');
    if (archivos.length === 0) {
      // Se espera el resultado: si el servidor lo rechaza (límite de velocidad, mensaje largo…) se muestra el motivo y no se pierde lo escrito
      const r = await dispatch(sendMessage(currentChat._id, contenido));
      if (r?.ok) adj.setError('');
      else {
        setText((actual) => actual || contenido);
        adj.setError(r?.error || 'No se pudo enviar el mensaje');
      }
      return;
    }
    // Con archivos la subida tarda: se espera el resultado y, si falla, se conserva todo para reintentar
    setEnviando(true);
    const r = await dispatch(sendMessage(currentChat._id, contenido, null, 'text', archivos));
    setEnviando(false);
    if (r?.ok) {
      adj.limpiar();
    } else {
      setText((actual) => actual || contenido);
      adj.setError(r?.error || 'No se pudo enviar el mensaje');
    }
  };

  // LA CONDICIÓN DEBE IR AQUÍ, DESPUÉS DE TODOS LOS HOOKS (useState, useEffect, etc.)
  if (location.pathname === '/mensajes') return null;
  // No mostrar el chat flotante en el panel de administración.
  if (location.pathname.startsWith('/admin')) return null;

  // Sin sesión real no se muestra la burbuja (el reducer inicia user en {} —truthy—,
  // por eso validamos que exista _id, no solo que el objeto no sea nulo).
  if (!authUser || !authUser._id) return null;

  const deletingOther = deletingChat?.participants?.find(p => String(p._id) !== String(authUser?._id));
  // Referencia para "Hoy" / "Ayer" en los separadores de la conversación y en la lista
  const ahora = new Date();

  return (
    <>
      {/* Modal confirmar eliminar conversación */}
      <Dialog open={!!deletingChat} onClose={() => setDeletingChat(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
          <Box sx={{
            width: 60, height: 60, borderRadius: '50%', bgcolor: (t) => alpha(t.palette.error.main, 0.25),
            display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2
          }}>
            <DeleteOutlineIcon sx={{ fontSize: 30, color: 'error.oscuro' }} />
          </Box>
          <Typography variant="h6" fontWeight={700} color="primary.main" mb={1}>
            ¿Eliminar conversación?
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Se eliminará la conversación con <strong>{deletingOther?.name} {deletingOther?.apellido}</strong>. Esta acción no se puede deshacer.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={() => setDeletingChat(null)}
              sx={{ textTransform: 'none', borderRadius: '8px', px: 3, borderColor: 'divider', color: 'text.secondary' }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                dispatch(deleteChat(deletingChat._id));
                setDeletingChat(null);
              }}
              sx={{ textTransform: 'none', borderRadius: '8px', px: 3, bgcolor: 'error.oscuro', '&:hover': { bgcolor: (t) => darken(t.palette.error.oscuro, 0.2) } }}
            >
              Eliminar
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Botón Flotante Principal */}
      {!isOpen && (
        <IconButton
          onClick={() => dispatch(toggleChat(true))}
          sx={{
            position: 'fixed', bottom: 30, right: 30,
            bgcolor: 'primary.main', color: 'white',
            width: 60, height: 60,
            boxShadow: (t) => `0 8px 32px ${alpha(t.palette.primary.main, 0.4)}`,
            '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.05)' },
            transition: 'all 0.3s ease',
            zIndex: 9999
          }}
        >
          <Badge badgeContent={totalUnread} color="error">
            <ForumIcon sx={{ fontSize: 30 }} />
          </Badge>
        </IconButton>
      )}

      {/* Ventana de Chat */}
      {isOpen && (
        <Paper
          elevation={24}
          sx={{
            position: 'fixed', bottom: 100, right: 30,
            width: { xs: '90vw', sm: 400 }, height: 600,
            borderRadius: '24px', overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            zIndex: 9999, border: 1, borderColor: 'divider'
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                {currentChat && (
                  <IconButton size="small" onClick={() => dispatch(setCurrentChat(null))} sx={{ color: 'white' }}>
                    <ArrowBackIcon />
                  </IconButton>
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" noWrap sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2, maxWidth: 210 }}>
                    {currentChat ? (temaTitulo(currentChat) || 'Conversación') : 'Mensajes'}
                  </Typography>
                  {currentChat && temaCategoria(currentChat) && (
                    <Typography variant="caption" noWrap sx={{ display: 'block', opacity: 0.75, maxWidth: 210 }}>
                      {temaCategoria(currentChat)}
                    </Typography>
                  )}
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title="Ver en pantalla completa">
                  <IconButton size="small" onClick={irChatCompleto} sx={{ color: 'white' }}>
                    <OpenInFullIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => dispatch(toggleChat(false))} sx={{ color: 'white' }}>
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {!currentChat ? (
            /* Lista de Chats */
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50' }}>
              {errorChat?.origen === 'iniciar' && errorChat.msg && (
                <Typography role="alert" variant="body2" sx={{ color: 'error.oscuro', bgcolor: (t) => alpha(t.palette.error.main, 0.25), borderRadius: '12px', p: 1.5, mb: 2 }}>
                  {errorChat.msg}
                </Typography>
              )}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress size={24} /></Box>
              ) : chats.length > 0 ? (
                chats.map(chat => {
                  const other = chat.participants.find(p => String(p._id) !== String(authUser?._id));
                  const userIsOnline = other && onlineUsers.some(id => String(id) === String(other?._id));
                  const avatar = other?.avatar ? (other.avatar.startsWith('http') ? other.avatar : `${urlImg}/users/${other._id}/${other.avatar}`) : '';
                  
                  return (
                    <Paper 
                      key={chat._id}
                      onClick={() => dispatch(setCurrentChat(chat))}
                      elevation={0}
                      sx={{ 
                        p: 2, mb: 1, borderRadius: '16px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 2,
                        bgcolor: 'white', border: 1, borderColor: 'sutil',
                        '&:hover': { bgcolor: 'sutil', transform: 'translateY(-2px)' },
                        transition: 'all 0.2s'
                      }}
                    >
                      {/* Punto verde de quien está en línea, igual que en /mensajes */}
                      <Badge color="success" variant="dot" invisible={!userIsOnline} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                        <Avatar src={avatar} sx={{ width: 48, height: 48 }} />
                      </Badge>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Nombre y cuándo fue el último mensaje, igual que en /mensajes */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }} noWrap>
                            {other?.name} {other?.apellido}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0, ml: 1, whiteSpace: 'nowrap' }}>
                            {fechaEnLista(chat.lastMessage?.createdAt, ahora)}
                          </Typography>
                        </Stack>
                        {temaTitulo(chat) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, my: 0.25 }}>
                            <LocalOfferOutlinedIcon sx={{ fontSize: 12, color: 'secondary.main', flexShrink: 0 }} />
                            <Typography variant="caption" noWrap sx={{ color: 'secondary.main', fontWeight: 600 }}>
                              {temaTitulo(chat)}
                            </Typography>
                          </Box>
                        )}
                        <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                          {chat.lastMessage?.content || 'Sin mensajes'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {chat.unreadCount?.[authUser?._id] > 0 && (
                          <Badge badgeContent={chat.unreadCount[authUser._id]} color="primary" sx={{ mr: 1 }} />
                        )}
                        <Tooltip title="Eliminar conversación">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletingChat(chat);
                            }}
                            sx={{
                              color: 'grey.400',
                              '&:hover': { color: 'error.oscuro', bgcolor: (t) => alpha(t.palette.error.main, 0.25) }
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  );
                })
              ) : (
                <Box sx={{ textAlign: 'center', mt: 4, opacity: 0.5 }}>
                  <ForumIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="body2">No tienes conversaciones aún</Typography>
                </Box>
              )}
            </Box>
          ) : (
            /* Detalle del Chat */
            <>
              <Box sx={{ px: 2, py: 1, bgcolor: 'white', borderBottom: 1, borderColor: 'sutil', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Avatar y nombre llevan al perfil de la otra persona */}
                <Tooltip title={rutaPerfil ? 'Ver perfil' : ''} describeChild>
                  <Box
                    {...(rutaPerfil && { component: RouterLink, to: rutaPerfil, onClick: alAbrirPerfil })}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0,
                      color: 'inherit', textDecoration: 'none',
                      ...(rutaPerfil && { cursor: 'pointer', '&:hover .nombre-chat': { textDecoration: 'underline' } })
                    }}
                  >
                    <Avatar
                      src={currentChat.participants.find(p => p._id !== authUser?._id)?.avatar ? (currentChat.participants.find(p => p._id !== authUser?._id).avatar.startsWith('http') ? currentChat.participants.find(p => p._id !== authUser?._id).avatar : `${urlImg}/users/${currentChat.participants.find(p => p._id !== authUser?._id)._id}/${currentChat.participants.find(p => p._id !== authUser?._id).avatar}`) : ''}
                      sx={{ width: 36, height: 36 }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography className="nombre-chat" variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>
                        {otherParticipant?.name} {otherParticipant?.apellido}
                      </Typography>
                      {/* Hasta que el servidor manda quién está conectado no se sabe: mejor no decir nada */}
                      {presenciaRecibida && (
                        <Typography variant="caption" sx={{ color: isOnline ? 'success.oscuro' : 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 6, height: 6, bgcolor: isOnline ? 'success.main' : 'text.secondary', borderRadius: '50%' }} />
                          {isOnline ? 'En línea' : 'Desconectado'}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Tooltip>
                <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                  <MenuChat chat={currentChat} nombre={otherParticipant?.name} zIndex={10000} />
                </Box>
              </Box>

              {soyProveedor && (
                <Box
                  onClick={irChatCompleto}
                  sx={{
                    px: 2, py: 1, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.2) }
                  }}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: 'primary.main', lineHeight: 1.3 }}>
                    Para enviar una <strong>cotización formal</strong> abre el chat completo.
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700, ml: 'auto', whiteSpace: 'nowrap' }}>
                    Abrir
                  </Typography>
                </Box>
              )}

              {soyCliente && hayCotizacion && (
                <Box
                  onClick={irChatCompleto}
                  sx={{
                    px: 2, py: 1, bgcolor: (t) => alpha(t.palette.secondary.main, 0.12), borderBottom: 1, borderColor: 'divider',
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.secondary.main, 0.2) }
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ color: 'primary.main', lineHeight: 1.3 }}>
                    Recibiste una <strong>cotización</strong>. Ábrela en el chat completo para verla.
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700, ml: 'auto', whiteSpace: 'nowrap' }}>
                    Abrir
                  </Typography>
                </Box>
              )}

              <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: 'grey.50', display: 'flex', flexDirection: 'column' }}>
                {messages.map((msg, index) => {
                  const getStrId = (id) => (typeof id === 'object' && id?._id ? id._id.toString() : String(id || ''));
                  const msgSenderId = getStrId(msg.sender);
                  const myId = getStrId(authUser?._id);
                  const isMe = msgSenderId === myId;
                  // El primer mensaje de cada día lleva arriba el día en que se mandó ("Hoy", "Ayer", "9 de octubre"…)
                  const dia = index === 0 || !mismoDia(messages[index - 1].createdAt, msg.createdAt) ? etiquetaDia(msg.createdAt, ahora) : '';

                  return (
                    <React.Fragment key={msg._id || index}>
                    {dia && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="caption" sx={{ px: 1, color: 'text.secondary', fontWeight: 600 }}>{dia}</Typography>
                        <Divider sx={{ flex: 1 }} />
                      </Box>
                    )}
                    <Box sx={{ alignSelf: isMe ? 'flex-end' : 'flex-start', mb: 2, maxWidth: '80%' }}>
                      <Box sx={{ 
                        p: 1.5, borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                        bgcolor: isMe ? 'primary.main' : 'white',
                        color: isMe ? 'white' : 'text.primary',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: isMe ? 'none' : 1, borderColor: 'sutil'
                      }}>
                        {!msg.soloAdjuntos && (
                          <Typography variant="body2" sx={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{msg.content}</Typography>
                        )}
                        <AdjuntosMensaje adjuntos={msg.adjuntos} />
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, textAlign: 'right', fontSize: '0.65rem' }}>
                          {horaMensaje(msg.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </Box>

              {currentChat.bloqueadoPorMi ? (
                <AvisoBloqueo chatId={currentChat._id} nombre={otherParticipant?.name} />
              ) : (
              <Box component="form" onSubmit={handleSend} sx={{ p: 2, bgcolor: 'white', borderTop: 1, borderColor: 'sutil' }}>
                <VistaPreviaAdjuntos archivos={adj.archivos} error={adj.error} onQuitar={adj.quitar} enviando={enviando} procesando={adj.procesando} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <BotonAdjuntar onArchivos={adj.agregar} disabled={enviando} sx={{ p: 0.75 }} />
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Escribe un mensaje..."
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    autoComplete="off"
                    inputProps={{ maxLength: MAX_TEXTO_CHAT }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'grey.50' } }}
                  />
                  <IconButton
                    type="submit" aria-label="Enviar mensaje" disabled={enviando || adj.procesando || (!text.trim() && adj.archivos.length === 0)}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                  >
                    {enviando ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              </Box>
              )}
            </>
          )}
        </Paper>
      )}
    </>
  );
};

export default ChatFloating;
